import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import {
  checkEmulationMode,
  EmulationMode,
  removeEmulationHeaders,
  usePermissions,
  usePersonalSettings,
  useUser,
} from '@tyro/api';
import { useResponsive } from '@tyro/core/hooks';
import {
  DocumentationReaderButton,
  useReaderActions,
} from '@tyro/documentation';
import { useTranslation } from '@tyro/i18n';
import { ErrorCircleIcon, HamburgerMenuIcon } from '@tyro/icons';
import { Link } from '@tyro/router/components';
import { AcademicNamespaceSessionSwitcher } from '@tyro/settings/components/academic-namespaces/academic-namespace-session-switcher';
import { useEffect, useMemo } from 'react';
import useOffSetTop from '../../../hooks/use-off-set-top';
import Searchbar from '../../search';
import { HEADER, NAV } from '../nav/config';
import AccountPopover from './account-popover';
import { NotificationsPopover } from './notifications-popover';
import { PrintEmergencyListButton } from './print-emergency-list-button';
import { TyronePopover } from './tyrone-popover';
import { WhatsNewPopover } from './whats-new-popover';

type Props = {
  isNavExpanded: boolean;
  onOpenNav?: VoidFunction;
};

export function Header({ isNavExpanded, onOpenNav }: Props) {
  const { t } = useTranslation(['common', 'documentation']);
  const { user, activeProfile } = useUser();
  const { isStaffUserWithPermission, isTyroUser, hasPermission } =
    usePermissions();
  const emulationMode = useMemo(() => checkEmulationMode(), [activeProfile]);
  const hasSchoolSettingsPermission = hasPermission(
    'ps:1:general_admin:write_school_settings',
  );
  const { security_summerMode_enabled: isSummerModeEnabled } =
    usePersonalSettings();
  const showSummerModeWarning =
    isSummerModeEnabled && (hasSchoolSettingsPermission || isTyroUser);

  const { open: openReader, navigateToPage } = useReaderActions();

  // Tyrone's docs-Q&A agent dispatches `tyro:open-doc` events when its
  // response cites a documentation page. Catch them here, open the reader
  // pane (navigateToPage only sets location/history; isOpen stays false
  // unless OPEN is dispatched), then navigate to the cited page.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (
        event as CustomEvent<{ spaceSlug: string; pageSlug: string }>
      ).detail;
      if (!detail?.spaceSlug || !detail?.pageSlug) return;
      openReader();
      navigateToPage(detail.spaceSlug, detail.pageSlug);
    };
    window.addEventListener('tyro:open-doc', handler);
    return () => window.removeEventListener('tyro:open-doc', handler);
  }, [openReader, navigateToPage]);

  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP);

  const renderContent = (
    <>
      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: 'text.primary' }}>
          <HamburgerMenuIcon />
        </IconButton>
      )}

      {isStaffUserWithPermission('ps:1:search:search') && <Searchbar />}

      {showSummerModeWarning && (
        <Tooltip title={t('common:summerModeEnabledWarningChipTooltip')}>
          <Chip
            variant="soft"
            color="warning"
            clickable
            icon={<ErrorCircleIcon sx={{ width: 20, height: 20 }} />}
            label={t('common:summerModeEnabledWarningChipLabel')}
            component={Link}
            to="/settings/system?module=security&category=summerMode&nameId=enabled"
          />
        </Tooltip>
      )}

      {emulationMode !== EmulationMode.None && (
        <Button
          onClick={() => {
            removeEmulationHeaders();
            window.location.replace('/');
          }}
          sx={{ ml: 1 }}
        >
          Stop emulation
        </Button>
      )}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
      >
        {isStaffUserWithPermission('ps:1:users:can_switch_academic_years') && (
          <AcademicNamespaceSessionSwitcher />
        )}
        <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
          <PrintEmergencyListButton />
          {isStaffUserWithPermission(
            'ps:1:documentation:read_documentation',
          ) && <DocumentationReaderButton />}
          <WhatsNewPopover />
          {(isTyroUser || user?.isEmulatingAsTyroUser) && <TyronePopover />}
          {!isTyroUser && <NotificationsPopover />}
        </Stack>
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={(theme) => ({
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        backgroundColor: theme.isLight
          ? alpha(theme.palette.slate[100], 0.72)
          : theme.palette.slate[100],
        backdropFilter: 'blur(10px)',
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(!isNavExpanded && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      })}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
